import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, ErrorBox, FreshnessStamp, Skeleton } from "../../components/ui";
import { useChannelEffectiveness, useChannelMutation, useChannels, type RecruitmentChannel } from "../../lib/api";
import { fmtNumber, fmtValue } from "../../lib/format";

const KIND_PILL: Record<string, string> = {
  Agency: "bg-violet-50 text-violet-700",
  "Job board": "bg-blue-50 text-brand-primary",
  "Careers site": "bg-green-50 text-status-green",
  "Internal portal": "bg-brand-grey-light text-brand-charcoal",
  Referral: "bg-amber-50 text-status-amber",
  Government: "bg-brand-grey-light text-brand-charcoal",
};

const EMPTY: Partial<RecruitmentChannel> = { channel_name: "", kind: "Job board", active: 1 };

/** Channels and agencies (WP-23.1, OS-0618): where a job description goes, and which of those places produce
 * people who are actually hired.
 *
 * Channels used to be a five-value list written down three times — a Select on the posting, a dict of links in
 * the API and an array in the SPA — with candidate source a fourth, unconstrained list that never agreed with
 * any of them. An agency could be recorded as somebody's source but could not be posted to. */
export default function Channels({ company }: { company?: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const reg = useChannels(true);
  const eff = useChannelEffectiveness(company);
  const save = useChannelMutation();
  const [editing, setEditing] = useState<Partial<RecruitmentChannel> | null>(null);
  const isHr = reg.data?.is_hr ?? false;

  if (reg.isError) return <div className="mt-6"><ErrorBox error={reg.error} onRetry={() => void reg.refetch()} /></div>;
  if (!reg.data) return <div className="mt-6 space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-40" />)}</div>;

  const money = (v: number | null | undefined) => (v ? fmtValue(v, "CDF", lng) : "–");

  return (
    <div className="mt-4 space-y-6" data-rec-channels>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-brand-grey">{t("rec.channelsHint")}</p>
        <div className="flex items-center gap-3">
          {eff.data && <FreshnessStamp lastUpdated={eff.data.as_of} />}
          {isHr && (
            <button onClick={() => setEditing(EMPTY)} data-rec-add-channel
              className="rounded bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white">{t("rec.addChannel")}</button>
          )}
        </div>
      </div>

      {eff.data && eff.data.attribution.pct != null && eff.data.attribution.unattributed > 0 && (
        <div className="rounded-lg border border-status-amber/40 bg-amber-50 px-4 py-2 text-sm text-status-amber" data-rec-attribution-gap>
          {t("rec.attributionGap", { n: eff.data.attribution.unattributed, total: eff.data.attribution.total, pct: eff.data.attribution.pct })}
        </div>
      )}

      <Card className="overflow-hidden" data-rec-effectiveness>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("rec.effectiveness")}</h2>
          <span className="text-xs text-brand-grey">{t("rec.effectivenessHint")}</span>
        </div>
        {eff.isError && <div className="p-3"><ErrorBox error={eff.error} onRetry={() => void eff.refetch()} /></div>}
        {!eff.data ? <Skeleton className="m-4 h-32" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("rec.channel")}</th>
                  <th className="px-2 py-2 text-right">{t("rec.stats.applicants")}</th>
                  <th className="px-2 py-2 text-right">{t("rec.stats.screened")}</th>
                  <th className="px-2 py-2 text-right">{t("rec.stats.shortlisted")}</th>
                  <th className="px-2 py-2 text-right">{t("rec.hired")}</th>
                  <th className="px-2 py-2 text-right">{t("rec.conversion")}</th>
                  <th className="px-4 py-2 text-right">{t("rec.costPerHire")}</th>
                </tr>
              </thead>
              <tbody>
                {eff.data.rows.map((r) => (
                  <tr key={r.name ?? "none"} className={`border-t border-brand-grey-light ${r.unattributed ? "bg-amber-50/40" : ""}`} data-rec-eff-row>
                    <td className="px-4 py-2">
                      <span className="font-medium">{r.unattributed ? t("rec.unattributed") : r.name}</span>
                      {r.kind && <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold ${KIND_PILL[r.kind] ?? ""}`}>{t(`rec.kind.${r.kind}`, r.kind)}</span>}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.applicants}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-brand-grey">{r.screened}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.shortlisted}</td>
                    <td className="px-2 py-2 text-right font-semibold tabular-nums">{r.hired}</td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {r.conversion_pct == null ? <span className="text-brand-grey" title={t("rec.noApplicants")}>–</span> : `${fmtNumber(r.conversion_pct, lng, 1)} %`}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(r.cost_per_hire)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden" data-rec-register>
        <div className="border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("rec.register")}</h2>
        </div>
        <ul className="divide-y divide-brand-grey-light">
          {reg.data.channels.map((c) => (
            <li key={c.name} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-rec-channel-row={c.name}>
              <div className="min-w-0">
                <span className="font-semibold">{c.channel_name}</span>
                <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold ${KIND_PILL[c.kind] ?? ""}`}>{t(`rec.kind.${c.kind}`, c.kind)}</span>
                {!c.active && <span className="ml-2 rounded bg-brand-grey-light px-1.5 py-0.5 text-[10px]">{t("rec.inactive")}</span>}
                <div className="mt-0.5 text-[11px] text-brand-grey">
                  {c.kind === "Agency"
                    ? [c.contact_person, c.contact_email, c.fee_percent ? t("rec.feeOf", { pct: c.fee_percent }) : null,
                       c.guarantee_days ? t("rec.guaranteeOf", { days: c.guarantee_days }) : null,
                       c.exclusive ? t("rec.exclusive") : null].filter(Boolean).join(" · ")
                    : (c.deep_link ?? t("rec.noLink"))}
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-brand-grey">
                <span>{t("rec.postingsCount", { n: c.postings, posted: c.posted })}</span>
                {isHr && <button onClick={() => setEditing(c)} data-rec-edit-channel className="font-semibold text-brand-primary hover:underline">{t("rec.edit")}</button>}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {editing && <ChannelForm channel={editing} onClose={() => setEditing(null)} onSave={(v) => {
        save.mutate({ method: "save_channel", params: v }, { onSuccess: () => setEditing(null) });
      }} pending={save.isPending} error={save.error} />}
    </div>
  );
}

function ChannelForm({ channel, onClose, onSave, pending, error }: {
  channel: Partial<RecruitmentChannel>;
  onClose: () => void;
  onSave: (values: Record<string, unknown>) => void;
  pending: boolean;
  error: unknown;
}) {
  const { t } = useTranslation();
  const [v, setV] = useState<Partial<RecruitmentChannel>>(channel);
  const set = (k: keyof RecruitmentChannel, value: unknown) => setV((p) => ({ ...p, [k]: value }));
  const isAgency = v.kind === "Agency";
  const field = "w-full rounded border border-brand-grey-light px-2 py-1 text-sm";

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" onClick={onClose}>
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto p-5" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <h2 className="font-heading text-lg font-semibold">{channel.name ? t("rec.editChannel") : t("rec.addChannel")}</h2>
        <div className="mt-4 grid gap-3">
          <label className="text-xs text-brand-grey">{t("rec.channel")}
            <input value={v.channel_name ?? ""} onChange={(e) => set("channel_name", e.target.value)} data-rec-channel-name
              disabled={!!channel.name} className={`${field} mt-1 disabled:bg-brand-grey-light/40`} />
          </label>
          <label className="text-xs text-brand-grey">{t("rec.kindLabel")}
            <select value={v.kind ?? "Job board"} onChange={(e) => set("kind", e.target.value)} data-rec-channel-kind className={`${field} mt-1`}>
              {["Job board", "Careers site", "Internal portal", "Referral", "Agency", "Government"].map((k) => (
                <option key={k} value={k}>{t(`rec.kind.${k}`, k)}</option>
              ))}
            </select>
          </label>
          {!isAgency && (
            <label className="text-xs text-brand-grey">{t("rec.whereToPost")}
              <input value={v.deep_link ?? ""} onChange={(e) => set("deep_link", e.target.value)} className={`${field} mt-1`} />
            </label>
          )}
          {isAgency && (
            <>
              <label className="text-xs text-brand-grey">{t("rec.contact")}
                <input value={v.contact_person ?? ""} onChange={(e) => set("contact_person", e.target.value)} className={`${field} mt-1`} />
              </label>
              <label className="text-xs text-brand-grey">{t("rec.contactEmail")}
                <input value={v.contact_email ?? ""} onChange={(e) => set("contact_email", e.target.value)} data-rec-channel-email className={`${field} mt-1`} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-brand-grey">{t("rec.fee")}
                  <input type="number" value={v.fee_percent ?? ""} onChange={(e) => set("fee_percent", Number(e.target.value))} className={`${field} mt-1`} />
                </label>
                <label className="text-xs text-brand-grey">{t("rec.guarantee")}
                  <input type="number" value={v.guarantee_days ?? ""} onChange={(e) => set("guarantee_days", Number(e.target.value))} className={`${field} mt-1`} />
                </label>
              </div>
              <label className="flex items-center gap-2 text-xs text-brand-grey">
                <input type="checkbox" checked={!!v.exclusive} onChange={(e) => set("exclusive", e.target.checked ? 1 : 0)} />
                {t("rec.exclusive")}
              </label>
              <label className="text-xs text-brand-grey">{t("rec.terms")}
                <textarea value={v.terms ?? ""} onChange={(e) => set("terms", e.target.value)} rows={3} className={`${field} mt-1`} />
              </label>
            </>
          )}
          <label className="flex items-center gap-2 text-xs text-brand-grey">
            <input type="checkbox" checked={v.active !== 0} onChange={(e) => set("active", e.target.checked ? 1 : 0)} />
            {t("rec.activeChannel")}
          </label>
        </div>
        {!!error && <div className="mt-3"><ErrorBox error={error} /></div>}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded border border-brand-grey-light px-3 py-1.5 text-sm">{t("common.cancel")}</button>
          <button disabled={pending || !v.channel_name} data-rec-save-channel
            onClick={() => onSave({ ...(channel.name ? { name: channel.name } : {}), ...v })}
            className="rounded bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
            {pending ? t("rec.saving") : t("rec.save")}
          </button>
        </div>
      </Card>
    </div>
  );
}
